// Text that formats properly, and lines that connect (SA, Features 4, 26 Oct 2026; David: "Slides that design like
// PowerPoint"). A box's words (and a layout's field's) can carry PowerPoint's paragraph settings on top of S5's look:
// letter spacing, space before and after each paragraph, a bullet mark and a number format for its lists (each level of
// a list then gets its own mark and a step smaller size, as PowerPoint's outline does), one to three columns, and
// "do not autofit" beside S5's shrink and grow. A line can be an elbow (right-angle bends, its middle bend where `bend`
// says) and either end can be attached to one of a box's four connection points (the middle of each side, as PowerPoint
// shows them): moving or resizing the box keeps the line on it (routeLines, run wherever a slide is cleaned, so the
// board, the server and agents agree), and removing the box leaves the line where it was.
//
// Every field here is optional and new: a slide without them is drawn exactly as before.

import type { SlideBox } from "./slideDesign";

// ---------------------------------------------------------------------------
// words

/** The marks a bulleted list can wear, as PowerPoint's Bullets menu offers them (its first level; deeper levels step through the rest). */
export const BULLETS = ["dot", "circle", "square", "dash", "arrow", "check"] as const;
export type BulletMark = (typeof BULLETS)[number];
export const BULLET_CHARS: Record<BulletMark, string> = { dot: "•", circle: "○", square: "▪", dash: "–", arrow: "➤", check: "✓" };
export const BULLET_NAMES: Record<BulletMark, string> = { dot: "Dot", circle: "Circle", square: "Square", dash: "Dash", arrow: "Arrow", check: "Check mark" };

/** The number formats a numbered list can wear, as PowerPoint's Numbering menu offers them. */
export const NUMBERINGS = ["1.", "1)", "a.", "a)", "i.", "A.", "I."] as const;
export type Numbering = (typeof NUMBERINGS)[number];
/** PowerPoint's own name for each (a:buAutoNum type). */
export const NUMBERING_PPT: Record<Numbering, string> = { "1.": "arabicPeriod", "1)": "arabicParenR", "a.": "alphaLcPeriod", "a)": "alphaLcParenR", "i.": "romanLcPeriod", "A.": "alphaUcPeriod", "I.": "romanUcPeriod" };

/** How deep a list goes: five levels, as PowerPoint's outline has. */
export const MAX_LEVELS = 5;
/** Each level's size against the box's, as PowerPoint's outline steps them down (28, 24, 20, 18, 18 pt in its own master). */
export const LEVEL_SCALE = [1, 0.88, 0.78, 0.72, 0.72];

/** A list level's mark: the chosen one first, then the next ones round, so each level reads apart from the one above it. */
export function bulletAt(first: BulletMark, level: number): BulletMark {
  const order: BulletMark[] = first === "dot" ? ["dot", "dash", "circle", "dash", "dot"] : [first, "dash", "dot", "dash", "dot"];
  const m = order[Math.min(level, order.length - 1)];
  return level > 0 && m === first ? "dot" : m;
}

/** What SA adds to a box's (or a field's) words, as kept. */
export interface TextMore {
  /** letter spacing, in ems (PowerPoint's Character Spacing) */
  letter?: number;
  /** space before and after each paragraph, in the slide's pixels (1600 by 900) */
  before?: number;
  after?: number;
  /** a bulleted list's mark (then each level gets its own mark and size) */
  bullet?: BulletMark;
  /** a numbered list's format */
  numbering?: Numbering;
  /** words in two or three columns */
  cols?: 2 | 3;
}

export const num = (v: unknown, lo: number, hi: number, digits = 2): number | undefined => {
  if (typeof v !== "number" || !Number.isFinite(v)) return undefined;
  const k = 10 ** digits;
  return Math.round(Math.max(lo, Math.min(hi, v)) * k) / k;
};

/** SA's word fields as kept (an unknown or default value left out). */
export function cleanTextMore(r: Record<string, unknown>): TextMore {
  const out: TextMore = {};
  const letter = num(r.letter, -0.1, 0.5, 3);
  if (letter) out.letter = letter;
  const before = num(r.before, 0, 400, 1);
  if (before) out.before = before;
  const after = num(r.after, 0, 400, 1);
  if (after) out.after = after;
  if (typeof r.bullet === "string" && (BULLETS as readonly string[]).includes(r.bullet)) out.bullet = r.bullet as BulletMark;
  if (typeof r.numbering === "string" && (NUMBERINGS as readonly string[]).includes(r.numbering)) out.numbering = r.numbering as Numbering;
  if (r.cols === 2 || r.cols === 3) out.cols = r.cols;
  return out;
}

/** Whether a box's words have any of SA's paragraph settings (else they are drawn exactly as before). */
export const hasTextMore = (b: TextMore) => b.letter !== undefined || b.before !== undefined || b.after !== undefined || !!b.bullet || !!b.numbering || !!b.cols;

// ---------------------------------------------------------------------------
// lines that connect

export const SIDES = ["top", "right", "bottom", "left"] as const;
export type Side = (typeof SIDES)[number];
/** Where a line's end is attached: a box by id, and one of its four connection points. */
export interface LineLink {
  box: string;
  at: Side;
}

/** What SA adds to a line, as kept. */
export interface LineMore {
  /** an elbow: right-angle bends rather than straight */
  elbow?: true;
  /** where an elbow's middle bend sits between its ends, 0 at the start, 1 at the end (0.5 unless moved) */
  bend?: number;
  /** its start's and its end's box, when attached */
  from?: LineLink;
  to?: LineLink;
}

const BOX_ID = /^[A-Za-z0-9_-]{2,40}$/;

function cleanLink(raw: unknown): LineLink | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.box !== "string" || !BOX_ID.test(r.box)) return undefined;
  const at = typeof r.at === "string" && (SIDES as readonly string[]).includes(r.at) ? (r.at as Side) : undefined;
  return at ? { box: r.box, at } : undefined;
}

/** SA's line fields as kept. */
export function cleanLineMore(r: Record<string, unknown>): LineMore {
  const out: LineMore = {};
  if (r.elbow === true) out.elbow = true;
  const bend = num(r.bend, -2, 3, 3);
  if (out.elbow && bend !== undefined && bend !== 0.5) out.bend = bend;
  const from = cleanLink(r.from);
  const to = cleanLink(r.to);
  if (from) out.from = from;
  if (to) out.to = to;
  return out;
}

type Pt = { x: number; y: number };
type Frame = { x: number; y: number; w: number; h: number; rot?: number };

const OUT: Record<Side, Pt> = { top: { x: 0, y: -1 }, right: { x: 1, y: 0 }, bottom: { x: 0, y: 1 }, left: { x: -1, y: 0 } };

/** A box's connection point on one side (the middle of the side, turned with the box), and the way out of it. */
export function sitePoint(f: Frame, at: Side): { p: Pt; out: Pt } {
  const cx = f.x + f.w / 2;
  const cy = f.y + f.h / 2;
  const o = OUT[at];
  const lx = (o.x * f.w) / 2;
  const ly = (o.y * f.h) / 2;
  const a = ((f.rot ?? 0) * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { p: { x: r2(cx + lx * c - ly * s), y: r2(cy + lx * s + ly * c) }, out: { x: o.x * c - o.y * s, y: o.x * s + o.y * c } };
}

/** Every connection point of a box, for the editor to show and snap to. */
export const sitesOf = (f: Frame) => SIDES.map((at) => ({ at, ...sitePoint(f, at) }));

export const r2 = (n: number) => Math.round(n * 100) / 100;

/** A line's two ends on the slide (shared with the editor's geometry). */
export function endsOf(b: Pick<SlideBox, "x" | "y" | "w" | "h" | "flipH" | "flipV">): { a: Pt; b: Pt } {
  return { a: { x: b.x + (b.flipH ? b.w : 0), y: b.y + (b.flipV ? b.h : 0) }, b: { x: b.x + (b.flipH ? 0 : b.w), y: b.y + (b.flipV ? 0 : b.h) } };
}

/** A line's box and direction from its two ends. */
export function boxFromEnds(a: Pt, b: Pt): { x: number; y: number; w: number; h: number; flipH?: true; flipV?: true } {
  return { x: r2(Math.min(a.x, b.x)), y: r2(Math.min(a.y, b.y)), w: r2(Math.abs(b.x - a.x)), h: r2(Math.abs(b.y - a.y)), ...(b.x < a.x ? { flipH: true as const } : {}), ...(b.y < a.y ? { flipV: true as const } : {}) };
}

/** Which way an elbow leaves an end: along the slide's width (h) or height (v): an attached end by its side, a free one by the longer way. */
function axisAt(link: Side | undefined, out: Pt | undefined, dx: number, dy: number): "h" | "v" {
  if (link && out) return Math.abs(out.x) >= Math.abs(out.y) ? "h" : "v";
  return Math.abs(dx) >= Math.abs(dy) ? "h" : "v";
}

/** How an elbow runs: its first and last legs' ways, so the editor, the face and the .pptx draw the same path. */
export function elbowKind(l: Pick<SlideBox, "x" | "y" | "w" | "h" | "flipH" | "flipV"> & LineMore, outs: { a?: Pt; b?: Pt } = {}): { first: "h" | "v"; last: "h" | "v" } {
  const e = endsOf(l);
  const dx = e.b.x - e.a.x;
  const dy = e.b.y - e.a.y;
  const first = axisAt(l.from?.at, outs.a ?? (l.from ? OUT[l.from.at] : undefined), dx, dy);
  // a free end goes on as the elbow began; an attached one leaves its side
  const last = l.to ? axisAt(l.to.at, outs.b ?? OUT[l.to.at], dx, dy) : first;
  return { first, last };
}

/**
 * An elbow's corners, from its start to its end: three legs (across, down, across, the middle one at `bend`) when it
 * leaves and arrives the same way, two (one bend) when it turns once, as PowerPoint's elbow connectors run.
 */
export function elbowPoints(l: Pick<SlideBox, "x" | "y" | "w" | "h" | "flipH" | "flipV"> & LineMore, outs: { a?: Pt; b?: Pt } = {}): Pt[] {
  const { a, b } = endsOf(l);
  const k = l.bend ?? 0.5;
  const { first, last } = elbowKind(l, outs);
  if (first === "h" && last === "h") {
    const mx = r2(a.x + k * (b.x - a.x));
    return [a, { x: mx, y: a.y }, { x: mx, y: b.y }, b];
  }
  if (first === "v" && last === "v") {
    const my = r2(a.y + k * (b.y - a.y));
    return [a, { x: a.x, y: my }, { x: b.x, y: my }, b];
  }
  return first === "h" ? [a, { x: b.x, y: a.y }, b] : [a, { x: a.x, y: b.y }, b];
}

/** A line's path on the slide: its two ends, or an elbow's corners. */
export function linePoints(l: SlideBox, outs: { a?: Pt; b?: Pt } = {}): Pt[] {
  if (!l.elbow) {
    const e = endsOf(l);
    return [e.a, e.b];
  }
  return elbowPoints(l, outs);
}

/** The way out of each attached end of a line, on this slide (a turned box turns its sides). */
export function linkOuts(l: LineMore, boxes: Map<string, Frame>): { a?: Pt; b?: Pt } {
  const fa = l.from ? boxes.get(l.from.box) : undefined;
  const fb = l.to ? boxes.get(l.to.box) : undefined;
  return { ...(fa && l.from ? { a: sitePoint(fa, l.from.at).out } : {}), ...(fb && l.to ? { b: sitePoint(fb, l.to.at).out } : {}) };
}

/**
 * Lines kept on the boxes they are attached to: each attached end moved to its box's connection point as the box stands
 * now (a line attached to a box that is gone keeps where it was, unattached). Boxes that are not attached lines come
 * back as they were, the same objects, so nothing else changes.
 */
export function routeLines<B extends SlideBox>(boxes: B[]): B[] {
  if (!boxes.some((b) => b.kind === "line" && (b.from || b.to))) return boxes;
  const frames = new Map<string, Frame>();
  for (const b of boxes) if (b.kind !== "line") frames.set(b.id, b);
  return boxes.map((l) => {
    if (l.kind !== "line" || !(l.from || l.to)) return l;
    const e = endsOf(l);
    const out: B = { ...l };
    let a = e.a;
    let z = e.b;
    const fa = l.from ? frames.get(l.from.box) : undefined;
    const fb = l.to ? frames.get(l.to.box) : undefined;
    if (l.from && fa) a = sitePoint(fa, l.from.at).p;
    else delete out.from;
    if (l.to && fb) z = sitePoint(fb, l.to.at).p;
    else delete out.to;
    // never onto itself: both ends on one point leaves the line a pixel long, still attached
    const g = boxFromEnds(a, z);
    out.x = g.x;
    out.y = g.y;
    out.w = Math.max(g.w, g.h < 1 ? 1 : 0);
    out.h = g.h;
    if (g.flipH) out.flipH = true;
    else delete out.flipH;
    if (g.flipV) out.flipV = true;
    else delete out.flipV;
    return out;
  });
}

/** The nearest connection point of any box to a point, within reach (for an end being dragged): skipping the line's own. */
export function nearestSite(boxes: SlideBox[], p: Pt, reach: number, skip?: string): (LineLink & { p: Pt }) | null {
  let best: (LineLink & { p: Pt; d: number }) | null = null;
  for (const b of boxes) {
    if (b.kind === "line" || b.id === skip) continue;
    for (const s of sitesOf(b)) {
      const d = Math.hypot(s.p.x - p.x, s.p.y - p.y);
      if (d <= reach && (!best || d < best.d)) best = { box: b.id, at: s.at, p: s.p, d };
    }
  }
  return best ? { box: best.box, at: best.at, p: best.p } : null;
}

/** How far a point is from a path of legs (for a click on an elbow). */
export function nearPath(pts: Pt[], p: Pt): number {
  let best = Infinity;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const l2 = dx * dx + dy * dy;
    const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
    best = Math.min(best, Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy)));
  }
  return best;
}

/** An elbow's bend handle: the middle of its middle leg (three legs), or null (two legs have no bend to move). */
export function bendHandle(pts: Pt[]): Pt | null {
  if (pts.length !== 4) return null;
  return { x: (pts[1].x + pts[2].x) / 2, y: (pts[1].y + pts[2].y) / 2 };
}

/** The bend an elbow's handle dragged to a point gives (three legs: across, down, across or down, across, down). */
export function bendAt(l: SlideBox, p: Pt, outs: { a?: Pt; b?: Pt } = {}): number {
  const { a, b } = endsOf(l);
  const { first } = elbowKind(l, outs);
  const span = first === "h" ? b.x - a.x : b.y - a.y;
  if (Math.abs(span) < 1) return 0.5;
  const k = first === "h" ? (p.x - a.x) / span : (p.y - a.y) / span;
  return Math.round(Math.max(-2, Math.min(3, k)) * 1000) / 1000;
}
